import { forwardRef, ReactNode } from 'react';

import classNames from 'classnames';

import { InfiniteScroll } from '../../InfiniteScroll';
import { CircularProgress } from '../../CircularProgress';
import { Box } from '../../Box';

import useStyles from './ListBox.styles';

/**
 * Свойства компонента {@link ListBox}
 */
interface ListBoxProps {
  /**
   * Флаг, указывающий, есть ли еще данные для загрузки.
   */
  hasMore: boolean;

  /**
   * Текущая длина данных в списке.
   */
  dataLength: number;

  /**
   * Функция, которая будет вызвана для загрузки следующей порции данных.
   */
  next: () => void;

  /**
   * Флаг, указывающий, что данные загружаются.
   */
  loading: boolean;

  /**
   * Флаг, указывающий на длинный список.
   */
  long?: boolean;

  /**
   * Дочерние элементы, которые будут отображаться в списке.
   */
  children?: ReactNode | ReactNode[];
}

/**
 * Компонент ListBox представляет собой контейнер для списка элементов с поддержкой бесконечной прокрутки.
 *
 * @internal
 * @param hasMore - Флаг, указывающий, есть ли еще данные для загрузки.
 * @param dataLength - Текущая длина данных в списке.
 * @param next - Функция, которая будет вызвана для загрузки следующей порции данных.
 * @param loading - Флаг, указывающий, что данные загружаются.
 * @param long - Флаг, указывающий на длинный список.
 * @param children - Дочерние элементы, которые будут отображаться в списке.
 * @param ref - Реф для доступа к DOM-элементу компонента.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ListBoxProps}
 */
export const ListBox = forwardRef<HTMLDivElement, ListBoxProps>(
  (
    { hasMore, dataLength, next, loading, long = false, children, ...rest },
    ref,
  ) => {
    const { classes } = useStyles();

    return (
      <div
        {...rest}
        id="autocomplete-input-scrollable"
        ref={ref}
        className={classNames(classes.scrollableWrapper, {
          [classes.longScrollableWrapper]: long,
        })}
      >
        <InfiniteScroll
          next={next}
          hasMore={hasMore}
          dataLength={dataLength}
          loader={
            loading && (
              <Box display="flex" justifyContent="center" p="16">
                <CircularProgress size={30} />
              </Box>
            )
          }
          endMessage={<div />}
          scrollId="autocomplete-input-scrollable"
        >
          <ul className={classes.listBox}>{children}</ul>
        </InfiniteScroll>
      </div>
    );
  },
);
